import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Spinner,
  IconButton,
  Link,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { formatDateToISO } from "@root/utils/date-helper";
import { FileIcon } from "@root/assets/icons/file-icon";
import { LongLeftArrow } from "@root/assets/icons";
import {
  ROUTE_NAMES,
  FILE_TYPE,
  PAGINATION_DEFAULTS,
  DOCUMENT_STATUS,
} from "@root/constants";
import { ReportItemContainer, ViewReportButton } from "./style";
import { useProjectsContext } from "@root/context/projects-context";
import { Paginate } from "@root/components/pagination";
import LeftArrowIcon from "@root/assets/icons/leftarrowicon";

const AllReports: React.FC = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchDocuments, documentsData, getDocumentsLoading, documentCount } =
    useProjectsContext();

  const [currentPage, setCurrentPage] = useState(1);
  const [moduleType, setModuleType] = useState<any>(null);
  const [moduleTitle, setModuleTitle] = useState("");

  // Get module type and title from URL params
  useEffect(() => {
    const type = searchParams.get("type");
    const title = searchParams.get("title");

    if (type && title) {
      setModuleType(parseInt(type));
      setModuleTitle(title);
    }
  }, [searchParams]);

  // Fetch documents when component mounts or page changes
  useEffect(() => {
    if (moduleType) {
      const payload = {
        page: currentPage,
        limit: PAGINATION_DEFAULTS.PAGESIZE,
        type: moduleType,
        status: DOCUMENT_STATUS.COMPLETED, // Always 8 as per requirements
      };
      fetchDocuments(payload);
    }
  }, [moduleType, currentPage]);

  const handleViewReport = (file: any) => {
    if (moduleType === FILE_TYPE.EXTRACTION_REVIEW) {
      navigate(`${ROUTE_NAMES.PERMIT_REVIEW}/${file.id}`);
    } else if (moduleType === FILE_TYPE.EXCEL_AUTO_COMPLETE) {
      navigate(`${ROUTE_NAMES.LEGAL_REVIEW}/${file.id}`);
    } else if (moduleType === FILE_TYPE.FINISHING_MATERIAL_REQUEST) {
      navigate(`${ROUTE_NAMES.MATERIAL_CAMPARISON}/${file.id}`);
    }
  };

  const handleBackClick = () => {
    navigate(ROUTE_NAMES.DASHBOARD);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <Box height="100%" display="flex" flexDirection="column" w="100%">
      {/* Fixed Header */}
      <Box flexShrink={0} p={6} pt={0} pb={4} ml={"30px"}>
        <Text
          display="inline-flex"
          alignItems="center"
          gap={2}
          color="#40599B"
          py={5}
          cursor="pointer"
          onClick={() => navigate("/dashboard")}
        >
          <LeftArrowIcon />
          {t("dashboard.goBack")}
        </Text>

        <Text fontSize="sm" color="gray.500">
          <Link href="/dashboard">{t("sidebar.dashboard")}</Link> /{" "}
          <Text as="span" fontWeight="bold" color="#252525" fontSize="14px">
            {moduleTitle}
          </Text>
        </Text>
      </Box>

      {/* Scrollable Content Area */}
      <Box flex="1" overflowY="auto" minHeight="0" px={6}>
        {getDocumentsLoading ? (
          <Flex height="100%" alignItems="center" justifyContent="center">
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="#40599B"
              size="xl"
            />
          </Flex>
        ) : (
          <VStack
            align="stretch"
            spacing={3}
            w="100%"
            maxW="900px"
            mx="auto"
            py={4}
          >
            {documentsData?.rows?.length ? (
              documentsData.rows.map((item: any, idx: number) => (
                <ReportItemContainer key={item?.id}>
                  <HStack spacing={3} flex="1" minWidth="0">
                    <FileIcon />
                    <Box flex="1" minWidth="0">
                      <Text fontWeight="semibold" noOfLines={1} color="black">
                        {item?.fileName_key || item?.fileName}
                      </Text>
                      <Text fontSize="xs" color="gray.500" noOfLines={1}>
                        {formatDateToISO(item?.createdAt)}
                      </Text>
                    </Box>
                  </HStack>
                  <ViewReportButton onClick={() => handleViewReport(item)}>
                    {t("dashboard.viewReport")}
                  </ViewReportButton>
                </ReportItemContainer>
              ))
            ) : (
              <Box
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
              >
                <Text color="gray.500" fontSize="lg" mb={2}>
                  {t("reports.noReportsFound")}
                </Text>
                <Text color="gray.400" fontSize="sm">
                  {t("reports.noReportsFoundDesc")}
                </Text>
              </Box>
            )}
          </VStack>
        )}
      </Box>

      {/* Fixed Pagination */}
      <Box flexShrink={0} mt={2} pb={5} bg="white" shadow="md">
        <Flex justify="center">
          <Paginate
            page={currentPage}
            count={documentCount || 0}
            pageSize={PAGINATION_DEFAULTS.PAGESIZE}
            onPageChange={handlePageChange}
          />
        </Flex>
      </Box>
    </Box>
  );
};

export default AllReports;
